import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * Landing page API service
 * All endpoints are public — no auth required
 * Used by landing, features, resources, company pages
 */
const landingApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const landingService = {
  // ── Blog ──
  getBlogPosts: async (params = {}) => {
    try {
      const { data } = await landingApi.get("/blog/posts", { params });
      return data;
    } catch (error) {
return { success: false, posts: [], pagination: {} };
    }
  },

  getBlogPost: async (slug) => {
    try {
      const { data } = await landingApi.get(`/blog/posts/${slug}`);
      return data;
    } catch (error) {
return { success: false, post: null };
    }
  },

  // ── Careers ──
  getCareerOpenings: async (department = "") => {
    try {
      const params = department ? { department } : {};
      const { data } = await landingApi.get("/careers/openings", { params });
      return data;
    } catch (error) {
return { success: false, openings: [], departments: [], totalActive: 0 };
    }
  },

  getCareerPerks: async () => {
    try {
      const { data } = await landingApi.get("/careers/perks");
      return data;
    } catch (error) {
return { success: false, perks: [] };
    }
  },

  submitApplication: async (openingId, formData) => {
    try {
      const { data } = await landingApi.post(
        `/careers/apply/${openingId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    } catch (error) {
      const status = error.response?.status;
      const message =
        error.response?.data?.message || "Failed to submit application.";
      return { success: false, message, status };
    }
  },

  // ── Contact ──
  submitContact: async ({ name, email, subject, message }) => {
    try {
      const { data } = await landingApi.post("/contact", {
        name,
        email,
        subject,
        message,
      });
      return data;
    } catch (error) {
      const status = error.response?.status;
      const message =
        error.response?.data?.message || "Failed to send message.";
      const errors = error.response?.data?.errors || [];
      return { success: false, message, errors, status };
    }
  },

  // ── Newsletter ──
  subscribeNewsletter: async (email, source = "landing") => {
    try {
      const { data } = await landingApi.post("/newsletter/subscribe", {
        email,
        source,
      });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to subscribe.";
      return { success: false, message, status: error.response?.status };
    }
  },

  // ── Platform Stats ──
  getPublicStats: async () => {
    try {
      const { data } = await landingApi.get("/stats/public");
      return data;
    } catch (error) {
return { success: false, stats: null };
    }
  },

  // ── System Status ──
  getSystemStatus: async () => {
    try {
      const { data } = await landingApi.get("/status");
      return data;
    } catch (error) {
return { success: false, overallStatus: "operational", services: [] };
    }
  },

  // ── Testimonials ──
  getTestimonials: async () => {
    try {
      const { data } = await landingApi.get("/testimonials");
      return data;
    } catch (error) {
return { success: false, featured: null, testimonials: [] };
    }
  },

  // ── Trusted Institutions ──
  getTrustedInstitutions: async () => {
    try {
      const { data } = await landingApi.get("/institutions/trusted");
      return data;
    } catch (error) {
return { success: false, institutions: [] };
    }
  },

  // ── Team Members ──
  getTeamMembers: async () => {
    try {
      const { data } = await landingApi.get("/team");
      return data;
    } catch (error) {
return { success: false, team: [] };
    }
  },

  // ── Company Milestones ──
  getMilestones: async () => {
    try {
      const { data } = await landingApi.get("/company/milestones");
      return data;
    } catch (error) {
return { success: false, milestones: [] };
    }
  },

  // ── FAQs ──
  getFaqs: async (category = "") => {
    try {
      const params = category ? { category } : {};
      const { data } = await landingApi.get("/faq", { params });
      return data;
    } catch (error) {
return { success: false, faqs: [] };
    }
  },
};

export default landingService;
